import { useEffect, useState } from "react";
import { MAINTENANCE_MODE } from "@/constants/site";

/**
 * Holiday / maintenance overlay.
 * Shows a yellow Bebas-Neue "Back Soon" notice over a blurred homepage.
 * Owner can bypass by visiting the site once with ?preview=1 — that flag
 * is persisted to localStorage so subsequent visits also show the full site.
 */
export default function MaintenanceOverlay() {
  const [bypass, setBypass] = useState(false);

  // Honour ?preview=1 (and remember it) so the owner can audit the live site
  // without disabling maintenance mode globally.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("preview") === "1") {
        localStorage.setItem("tp-preview", "1");
      }
      if (localStorage.getItem("tp-preview") === "1") setBypass(true);
    } catch (_) {
      /* storage unavailable — safe to ignore */
    }
  }, []);

  useEffect(() => {
    if (!MAINTENANCE_MODE || bypass) return;
    // Lock scroll by fixing the body in place. This is the one technique that
    // reliably stops both wheel/touch scrolling AND window.scrollBy across
    // iOS Safari, Android Chrome and desktop browsers.
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      document.documentElement.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [bypass]);

  if (!MAINTENANCE_MODE || bypass) return null;

  return (
    <div
      data-testid="maintenance-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Site paused for holiday"
      className="fixed inset-0 z-[100] flex items-center justify-center px-5 overflow-hidden overscroll-contain"
      style={{
        backdropFilter: "blur(14px) saturate(1.1)",
        WebkitBackdropFilter: "blur(14px) saturate(1.1)",
        background: "rgba(10, 10, 10, 0.55)",
        touchAction: "none",
      }}
      onWheel={(e) => e.preventDefault()}
      onTouchMove={(e) => e.preventDefault()}
    >
      <div className="max-w-xl w-full text-center">
        {/* Yellow Bebas Neue headline */}
        <h1
          data-testid="maintenance-heading"
          className="font-bebas text-[#FFD400] leading-[0.95] tracking-[0.01em] drop-shadow-[0_4px_20px_rgba(0,0,0,0.6)]"
          style={{ fontSize: "clamp(3.5rem, 14vw, 7.5rem)" }}
        >
          Back Soon
        </h1>

        <p className="mt-5 text-white/90 text-base sm:text-lg font-medium leading-relaxed">
          TyresPro is currently closed. We'll be back on the road fitting tyres
          across Liverpool and surrounding areas very soon.
        </p>

        <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-white/50 font-semibold">
          TyresPro Ltd · Liverpool
        </div>
      </div>
    </div>
  );
}
